import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import ProductImageGallery from './components/ProductImageGallery';
import Stars from './components/Stars';
import ReviewCard from './components/ReviewCard';
import AdUnit from '@/components/AdUnit';
import AdSidebarStack from '@/components/AdSidebarStack';
import { supabase } from '@/lib/supabase';
import { useWishlist } from '@/hooks/useWishlist';
import { usePageMeta } from '@/hooks/usePageMeta';

const SIDEBAR_SLOT = import.meta.env.VITE_AD_SLOT_SIDEBAR as string | undefined;
const LEADERBOARD_SLOT = import.meta.env.VITE_AD_SLOT_LEADERBOARD as string | undefined;

interface ProductData {
  id: string;
  name: string;
  brand: string;
  rating: number;
  reviews_count: number;
  description: string;
  price: number;
  original_price: number | null;
  discount: number;
  image: string;
  badge: string;
  category: string;
  in_stock: boolean;
  images: string[];
  highlights: string[];
  specs: { label: string; value: string }[];
  pros: string[];
  cons: string[];
  userReviews: {
    id: string;
    user_name: string;
    rating: number;
    title: string;
    body: string;
    created_at: string;
  }[];
  whereToBuy: {
    id: string;
    retailer: string;
    price: number;
    url: string;
  }[];
}

interface RelatedProduct {
  id: string;
  name: string;
  brand: string;
  rating: number;
  price: number;
  original_price: number | null;
  image: string;
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const mainContentRef = useRef<HTMLDivElement>(null);
  const { isWishlisted, toggle: toggleWishlist } = useWishlist();
  const [activeTab, setActiveTab] = useState<'details' | 'reviews'>('details');
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [product, setProduct] = useState<ProductData | null>(null);
  const [related, setRelated] = useState<RelatedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  usePageMeta(
    product ? `${product.name} Review & Price` : undefined,
    product ? `${product.name} by ${product.brand}. Real review, pros & cons, and where to buy. ${product.description.slice(0, 120)}` : undefined
  );

  useEffect(() => {
    if (!id) return;
    const loadProduct = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: prod, error: prodErr } = await supabase.from('products').select('*').eq('id', id).maybeSingle();
        if (prodErr) throw prodErr;
        if (!prod) { setError('Product not found'); setLoading(false); return; }

        const [
          { data: imgs }, { data: highlights }, { data: specs },
          { data: pros }, { data: cons }, { data: reviews },
          { data: whereToBuy }, { data: relatedData },
        ] = await Promise.all([
          supabase.from('product_images').select('image_url').eq('product_id', id).order('sort_order'),
          supabase.from('product_highlights').select('text').eq('product_id', id).order('sort_order'),
          supabase.from('product_specs').select('label,value').eq('product_id', id).order('sort_order'),
          supabase.from('product_pros').select('text').eq('product_id', id).order('sort_order'),
          supabase.from('product_cons').select('text').eq('product_id', id).order('sort_order'),
          supabase.from('product_reviews').select('*').eq('product_id', id).order('created_at', { ascending: false }),
          supabase.from('where_to_buy').select('*').eq('product_id', id).order('sort_order'),
          supabase.from('products').select('id,name,brand,rating,price,original_price,image').neq('id', id).limit(4),
        ]);

        setProduct({
          ...prod,
          images: (imgs || []).map(i => i.image_url),
          highlights: (highlights || []).map(h => h.text),
          specs: specs || [],
          pros: (pros || []).map(p => p.text),
          cons: (cons || []).map(c => c.text),
          userReviews: reviews || [],
          whereToBuy: whereToBuy || [],
        } as ProductData);
        setRelated(relatedData || []);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [id]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const ratingBreakdown = [5, 4, 3, 2, 1].map((star) => {
    if (!product) return { star, count: 0, pct: 0 };
    const total = product.userReviews.length || product.reviews_count;
    const starCounts = [5, 4, 3, 2, 1].map(s => product.userReviews.filter(r => r.rating === s).length);
    const count = starCounts[5 - star];
    return { star, count, pct: total > 0 ? Math.round((count / total) * 100) : 0 };
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-ink-900 antialiased">
        <Header />
        <main className="pt-24 md:pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center text-ink-400 text-sm py-20">Loading product...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-white text-ink-900 antialiased">
        <Header />
        <main className="pt-24 md:pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <p className="text-rose-600 text-sm mb-3">{error || 'Product not found'}</p>
            <Link to="/search" className="h-9 px-4 rounded-lg bg-ink-900 text-white text-sm font-semibold whitespace-nowrap inline-flex items-center">Browse Products</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-ink-900 antialiased">
      <Header />

      <main className="pt-24 md:pt-32 pb-16">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-ink-500 mb-6">
            <Link to="/" className="hover:text-cta-600 transition whitespace-nowrap">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-base"></i>
            </span>
            <Link to={`/search?category=${product.category.toLowerCase()}`} className="hover:text-cta-600 transition whitespace-nowrap">{product.category}</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-base"></i>
            </span>
            <span className="text-ink-800 font-medium truncate">{product.brand}</span>
          </nav>
        </div>

        {/* Product Section */}
        <section className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-14">
            {/* Image Gallery */}
            <ProductImageGallery
              images={product.images}
              productName={product.name}
              discount={product.discount}
              badge={product.badge}
            />

            {/* Product Info */}
            <div className="flex flex-col">
              <p className="text-xs font-semibold uppercase tracking-widest text-cta-600">{product.brand}</p>
              <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold text-ink-900 leading-tight tracking-tight">
                {product.name}
              </h1>

              <div className="mt-3 flex items-center gap-3">
                <Stars rating={product.rating} />
                <span className="text-sm font-semibold text-ink-800">{product.rating}</span>
                <a href="#reviews" className="text-sm text-ink-500 hover:text-cta-600 transition whitespace-nowrap">
                  ({product.reviews_count.toLocaleString()} reviews)
                </a>
                <span className="text-ink-300">|</span>
                {product.in_stock ? (
                  <span className="text-xs text-emerald-600 font-semibold whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block mr-1"></span>
                    In Stock
                  </span>
                ) : (
                  <span className="text-xs text-rose-600 font-semibold whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 inline-block mr-1"></span>
                    Out of Stock
                  </span>
                )}
              </div>

              <div className="mt-5 flex items-baseline gap-3">
                <span className="font-display text-4xl font-bold text-cta-600">${product.price}</span>
                {product.original_price && product.original_price > product.price && (
                  <>
                    <span className="text-lg text-ink-400 line-through">${product.original_price}</span>
                    <span className="h-6 px-2 rounded-md bg-cta-100 text-cta-700 text-xs font-bold flex items-center whitespace-nowrap">
                      Save ${(product.original_price - product.price).toFixed(0)}
                    </span>
                  </>
                )}
              </div>
              <p className="mt-1.5 text-xs text-ink-400">Prices may vary based on your location and retailer.</p>

              <p className="mt-5 text-ink-600 leading-relaxed">{product.description}</p>

              <div className="mt-6 p-5 rounded-2xl bg-ink-50 border border-ink-100">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-3">Key Highlights</p>
                <ul className="space-y-2.5">
                  {product.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-ink-700">
                      <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-4">Where to Buy</p>
                <div className="grid grid-cols-2 gap-3">
                  {product.whereToBuy.map((w, i) => {
                    const isBest = i === 0;
                    const retailerIcons: Record<string, string> = {
                      'Amazon': 'ri-amazon-line',
                      'Best Buy': 'ri-store-2-line',
                      'B&H Photo': 'ri-camera-lens-line',
                      'Sony Direct': 'ri-store-line',
                    };
                    return (
                      <a
                        key={w.retailer}
                        href={w.url}
                        target="_blank"
                        rel="nofollow noopener"
                        className={`group relative flex flex-col gap-3 p-4 rounded-2xl border-2 transition-all duration-300 hover:-translate-y-1 ${
                          isBest
                            ? 'border-cta-500 bg-cta-50/40 hover:border-cta-600 hover:bg-cta-50/70'
                            : 'border-ink-200 bg-white hover:border-cta-400 hover:bg-cta-50/40'
                        }`}
                      >
                        {isBest && (
                          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 h-5 px-2.5 rounded-full bg-cta-500 text-white text-[10px] font-bold uppercase tracking-wide whitespace-nowrap flex items-center">
                            Best Deal
                          </span>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <span className={`w-10 h-10 rounded-xl flex items-center justify-center transition ${
                              isBest
                                ? 'bg-cta-500 text-white group-hover:bg-cta-600'
                                : 'bg-ink-100 text-ink-600 group-hover:bg-cta-100 group-hover:text-cta-600'
                            }`}>
                              <i className={`${retailerIcons[w.retailer] || 'ri-shopping-bag-line'} text-lg`}></i>
                            </span>
                            <span className="text-sm font-bold text-ink-900">{w.retailer}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`font-display text-xl font-bold whitespace-nowrap ${
                              isBest ? 'text-cta-600' : 'text-ink-900'
                            }`}>
                              ${w.price}
                            </span>
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 group-hover:translate-x-0.5 ${
                              isBest
                                ? 'bg-cta-500 text-white'
                                : 'bg-ink-100 text-ink-500 group-hover:bg-cta-500 group-hover:text-white'
                            }`}>
                              <i className="ri-arrow-right-up-line text-sm"></i>
                            </span>
                          </div>
                        </div>
                        {isBest && (
                          <div className="flex items-center gap-1.5 pt-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                            <span className="text-[11px] font-medium text-emerald-700">Lowest price from trusted retailer</span>
                          </div>
                        )}
                      </a>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 flex items-center gap-4">
                <button
                  onClick={() => toggleWishlist(product.id)}
                  className="flex items-center gap-1.5 text-sm transition whitespace-nowrap"
                  style={{ color: isWishlisted(product.id) ? '#f97316' : undefined }}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={isWishlisted(product.id) ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  </span>
                  {isWishlisted(product.id) ? 'Saved to Wishlist' : 'Save to Wishlist'}
                </button>
                <span className="text-ink-300">|</span>
                <div className="relative">
                  <button
                    onClick={() => setShareOpen(!shareOpen)}
                    className="flex items-center gap-1.5 text-sm text-ink-600 hover:text-cta-600 transition whitespace-nowrap"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-share-forward-line"></i>
                    </span>
                    Share
                  </button>
                  {shareOpen && (
                    <div className="absolute bottom-full left-0 mb-2 bg-white rounded-xl border border-ink-200 shadow-card p-2 flex items-center gap-1 z-20 animate-fade-in">
                      <button
                        onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${product.name} - $${product.price}`)}&url=${encodeURIComponent(window.location.href)}`, '_blank')}
                        className="w-9 h-9 rounded-lg hover:bg-ink-50 flex items-center justify-center text-ink-600 transition"
                        aria-label="Share on X"
                      >
                        <i className="ri-twitter-x-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                        className="w-9 h-9 rounded-lg hover:bg-ink-50 flex items-center justify-center text-ink-600 transition"
                        aria-label="Share on Facebook"
                      >
                        <i className="ri-facebook-line text-lg"></i>
                      </button>
                      <button
                        onClick={handleCopyLink}
                        className="w-9 h-9 rounded-lg hover:bg-ink-50 flex items-center justify-center text-ink-600 transition"
                        aria-label="Copy link"
                      >
                        <i className={copied ? 'ri-check-line text-emerald-500' : 'ri-link text-lg'}></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Specs & Reviews Tabs */}
        <section id="reviews" className="max-w-7xl mx-auto px-4 md:px-6 mt-16 md:mt-20">
          <div className="flex gap-8">
            {/* Main content */}
            <div className="flex-1 min-w-0" ref={mainContentRef}>
              <div className="flex items-center gap-1 p-1 bg-ink-50 rounded-full w-fit mb-8">
                {[
                  { key: 'details', label: 'Specifications' },
                  { key: 'reviews', label: `Reviews (${product.reviews_count.toLocaleString()})` },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key as 'details' | 'reviews')}
                    className={`h-10 px-5 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                      activeTab === t.key
                        ? 'bg-white text-ink-900 shadow-soft'
                        : 'text-ink-500 hover:text-ink-800'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {activeTab === 'details' && (
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-display text-xl font-bold text-ink-900 mb-5">Technical Specifications</h3>
                    <div className="bg-white rounded-2xl border border-ink-100 overflow-hidden">
                      {product.specs.map((s, i) => (
                        <div
                          key={s.label}
                          className={`flex items-center justify-between px-5 py-3.5 ${
                            i < product.specs.length - 1 ? 'border-b border-ink-100' : ''
                          }`}
                        >
                          <span className="text-sm text-ink-500">{s.label}</span>
                          <span className="text-sm font-semibold text-ink-900">{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-display text-xl font-bold text-ink-900 mb-5">Pros &amp; Cons</h3>
                    <div className="space-y-4">
                      <div className="bg-white rounded-2xl border border-ink-100 p-5">
                        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-3">Pros</p>
                        <ul className="space-y-2">
                          {product.pros.map((p) => (
                            <li key={p} className="flex items-start gap-2.5 text-sm text-ink-700">
                              <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-check-line text-xs"></i>
                              </span>
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-white rounded-2xl border border-ink-100 p-5">
                        <p className="text-xs font-semibold uppercase tracking-wider text-rose-600 mb-3">Cons</p>
                        <ul className="space-y-2">
                          {product.cons.map((c) => (
                            <li key={c} className="flex items-start gap-2.5 text-sm text-ink-700">
                              <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-close-line text-xs"></i>
                              </span>
                              {c}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 space-y-4">
                    <h3 className="font-display text-xl font-bold text-ink-900 mb-5">Customer Reviews</h3>
                    {product.userReviews.map((r) => (
                      <ReviewCard key={r.id} review={{
                        id: r.id,
                        user: r.user_name,
                        rating: r.rating,
                        date: new Date(r.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
                        title: r.title,
                        body: r.body,
                      }} />
                    ))}
                  </div>

                  <div>
                    <h3 className="font-display text-xl font-bold text-ink-900 mb-5">Rating Breakdown</h3>
                    <div className="bg-white rounded-2xl border border-ink-100 p-5 space-y-3">
                      <div className="flex items-center gap-4 pb-3 border-b border-ink-100">
                        <div className="text-center">
                          <p className="font-display text-4xl font-bold text-ink-900">{product.rating}</p>
                          <Stars rating={product.rating} size="sm" />
                          <p className="text-xs text-ink-500 mt-1">{product.reviews_count.toLocaleString()} reviews</p>
                        </div>
                      </div>
                      {ratingBreakdown.map((r) => (
                        <div key={r.star} className="flex items-center gap-3">
                          <span className="text-xs font-medium text-ink-600 whitespace-nowrap w-8">{r.star} star</span>
                          <div className="flex-1 h-2 rounded-full bg-ink-100 overflow-hidden">
                            <div className="h-full rounded-full bg-amber-500 transition" style={{ width: `${r.pct}%` }}></div>
                          </div>
                          <span className="text-xs text-ink-400 whitespace-nowrap w-10 text-right">{r.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <aside className="hidden xl:block w-[300px] flex-shrink-0">
              <AdSidebarStack slot={SIDEBAR_SLOT} targetRef={mainContentRef} />
            </aside>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
          <AdUnit slot={LEADERBOARD_SLOT} label="Advertisement" style={{ minHeight: 90 }} />
        </div>

        {/* Related Products */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-cta-600">You May Also Like</span>
              <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-ink-900 tracking-tight">
                Compare with similar products
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                className="product-card group relative bg-white rounded-2xl border border-ink-100 overflow-hidden flex flex-col"
              >
                <div className="relative aspect-square bg-ink-50 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="product-image w-full h-full object-contain p-4"
                  />
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{p.brand}</p>
                  <h3 className="mt-1 font-semibold text-ink-900 text-sm leading-snug line-clamp-2">{p.name}</h3>
                  <div className="mt-2 flex items-center gap-1.5">
                    <Stars rating={p.rating} size="sm" />
                    <span className="text-xs font-semibold text-ink-800">{p.rating}</span>
                  </div>
                  <div className="mt-auto pt-3 flex items-baseline gap-2">
                    <span className="font-display text-lg font-bold text-cta-600">${p.price}</span>
                    {p.original_price && p.original_price > p.price && (
                      <span className="text-xs text-ink-400 line-through">${p.original_price}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}